/**
 * 广告位统一配置
 * 集中管理所有广告位在不同广告源（云纵/伯斯特/P云/群蜂）下的 ID
 *
 * 切换广告源：修改 envsetting.js 中的 adFlag
 *   '1' = 云纵
 *   '2' = 伯斯特
 *   '3' = P云
 *   '4' = 群蜂
 *
 * 支付页顶部/底部双 Banner（EXIT_ORDER_TOP_BANNER / EXIT_ORDER_BOTTOM_BANNER）：
 *   - 使用 source 字段指定当前启用的广告源（yunzong | bosite | pcloud | qunfeng）
 *   - 仅填写 source 对应列的广告位 ID，其余列留空
 *   - 换厂商：改 source + 填新厂商 ID，无需改页面代码（PayBannerAd 自动选组件）
 *
 * 添加新广告源：
 *   1. 在下方 AD_POSITIONS 中添加新字段（如 xxx: '新ID'）
 *   2. 在 components/ads/ 下创建新组件
 *   3. 在 Adver.vue 中添加条件渲染
 *   4. 在 envsetting.js 注释中说明新 adFlag 值
 */

/**
 * 广告位配置
 * key = 广告位标识（代码中使用）
 * value = { yunzong, bosite, pcloud, qunfeng } 各广告源对应的 ID
 *         null 或空字符串 = 该广告源未配置此广告位（不展示）
 *
 * ┌──────────────────────┬─────────────────────────────────────────────────────────────────────┬────────┬───────┬───────┬────────────────────┐
 * │ 广告位标识            │ 使用页面                                                             │ 类型   │ 云纵   │ 群蜂   │ P云                 │
 * ├──────────────────────┼─────────────────────────────────────────────────────────────────────┼────────┼───────┼───────┼────────────────────┤
 * │ HOME_BANNER          │ 首页(UserHome.vue)                                                  │ BANNER │ ✅    │ ✅    │ 37                  │
 * │ HOME_POPUP           │ 首页弹窗-场内付(UserHome.vue)                                        │ 弹窗   │ ✅    │ ✅    │ 41                  │
 * │ EXIT_ORDER_BANNER    │ 出口码/支付页(旧单源，非支付页共存场景)                                │ BANNER │ ✅    │ ✅    │ 40                  │
 * │ EXIT_ORDER_TOP_BANNER     │ 支付页顶部横幅(PayMoney/EscapeOut，与底部共存，source 指定厂商)   │ BANNER │ 可配  │ 可配  │ 可配                │
 * │ EXIT_ORDER_BOTTOM_BANNER  │ 支付页底部横幅(PayMoney/EscapeOut，与顶部共存，source 指定厂商)   │ BANNER │ 可配  │ 可配  │ 可配                │
 * │ EXIT_ORDER_POPUP     │ 支付页弹窗(PayMoney.vue)                                            │ 弹窗   │ ✅    │ -     │ 123                 │
 * │ VISITOR_AD_1         │ 无牌车入场页(vh_p_n_in.vue) / 访客车登记页(vh_visitor_in.vue)        │ BANNER │ ✅    │ -     │ 141                 │
 * │ VISITOR_AD_2         │ 无牌车入场成功页(vh_p_n_in.vue)                                      │ BANNER │ ✅    │ -     │ 141                 │
 * │ PAY_COMPLETE_BANNER  │ 支付完成页(PayComplete.vue)                                          │ BANNER │ ✅    │ -     │ 141                 │
 * │ PAY_COUPON_BANNER    │ PayMoney特惠券包(PayMoney.vue, 通过pyunAdvertService调用)            │ BANNER │ -     │ -     │ 869969967989137408  │
 * └──────────────────────┴─────────────────────────────────────────────────────────────────────┴────────┴───────┴───────┴────────────────────┘
 */
export const AD_POSITIONS = {
  // 首页底部 BANNER（UserHome.vue）
  HOME_BANNER: {
    yunzong: '501008286821191680',
    bosite: '',
    pcloud: '37',
    qunfeng: 'GP2605251540150532511'
  },
  // 首页弹窗广告（UserHome.vue 场内付场景）
  HOME_POPUP: {
    yunzong: '1216033088880467968',
    bosite: '',
    pcloud: '41',
    qunfeng: 'GP2605251539597413512'
  },
  // 出口码/支付页面 BANNER（其他页面仍按 adFlag 单源展示）
  EXIT_ORDER_BANNER: {
    yunzong: '744202053102985216',
    bosite: '',
    pcloud: '40',
    qunfeng: 'GP2606091357518531962'
  },
  // 支付页顶部 BANNER（与底部共存；source 指定当前启用的广告源，不受 adFlag 互斥限制）
  EXIT_ORDER_TOP_BANNER: {
    source: 'pcloud',
    yunzong: '',
    bosite: '',
    pcloud: '40',
    qunfeng: ''
  },
  // 支付页底部 BANNER（与顶部共存；source 指定当前启用的广告源）
  EXIT_ORDER_BOTTOM_BANNER: {
    source: 'qunfeng',
    yunzong: '',
    bosite: '',
    pcloud: '',
    qunfeng: 'GP2606091357518531962'
  },
  // 出口码/支付页面弹窗广告
  EXIT_ORDER_POPUP: {
    yunzong: '1216033088880467968',
    bosite: '',
    pcloud: '123',
    qunfeng: 'GP2606091358540746925'
  },
  // 无牌车入场页面广告位、车辆登记
  VISITOR_AD_1: {
    yunzong: '501052411046072320',
    bosite: '',
    pcloud: '141',
    qunfeng: 'GP2606091358165843848'
  },
  // 无牌车入场成功页面 banner
  VISITOR_AD_2: {
    yunzong: '744201726840659968',
    bosite: '',
    pcloud: '141',
    qunfeng: 'GP2606091358165843848'
  },
  // 支付完成页 banner
  PAY_COMPLETE_BANNER: {
    yunzong: '501052502699999232',
    bosite: '',
    pcloud: '141',
    qunfeng: 'GP2606091358356604881'
  },
  // PayMoney 特惠券包（P云专属，通过 pyunAdvertService 拦截支付流程）
  PAY_COUPON_BANNER: {
    yunzong: '',
    bosite: '',
    pcloud: '869969967989137408',
    qunfeng: ''
  }
}

/** 支持的广告源 key（与 AD_POSITIONS 各槽位字段一致，不含 source） */
export const AD_SOURCE_KEYS = ['yunzong', 'bosite', 'pcloud', 'qunfeng']

/**
 * 支付页等多槽位场景：按槽位配置的 source 解析当前启用的广告
 * @param {string} positionKey 如 EXIT_ORDER_TOP_BANNER
 * @returns {{ source: string, id: string }|null}
 */
export function getBannerSlotConfig (positionKey) {
  const pos = AD_POSITIONS[positionKey]
  if (!pos) {
    console.warn('[AdConfig] 未知广告位:', positionKey)
    return null
  }
  const source = pos.source
  if (!source || AD_SOURCE_KEYS.indexOf(source) === -1) {
    return null
  }
  const id = pos[source]
  if (!id) {
    return null
  }
  return { source: source, id: id }
}

/**
 * 槽位是否已配置并可展示
 * @param {string} positionKey
 * @returns {boolean}
 */
export function hasBannerSlot (positionKey) {
  return getBannerSlotConfig(positionKey) !== null
}

/**
 * 获取指定广告位在当前广告源下的 ID
 * 不降级：当前广告源未配置则返回 null
 * @param {string} positionKey - 广告位标识（如 'HOME_BANNER'）
 * @param {string} [adFlag] - 广告源标识，不传则从 envsetting 读取
 * @returns {string|null} 广告位 ID，null 表示当前广告源未配置
 */
export function getAdPositionId (positionKey, adFlag) {
  const envsetting = require('./envsetting')
  const flag = adFlag || envsetting.adFlag
  const pos = AD_POSITIONS[positionKey]
  if (!pos) {
    console.warn('[AdConfig] 未知广告位:', positionKey)
    return null
  }

  const flagMap = { '1': 'yunzong', '2': 'bosite', '3': 'pcloud', '4': 'qunfeng' }
  const sourceKey = flagMap[flag] || 'yunzong'

  // 不降级，当前广告源有就返回，没有就返回 null
  return pos[sourceKey] || null
}

/**
 * 获取指定广告位在 P云 下的 spaceId
 * @param {string} positionKey - 广告位标识
 * @returns {string|null} P云 spaceId，null 表示 P云未配置此广告位
 */
export function getPyunSpaceId (positionKey) {
  const pos = AD_POSITIONS[positionKey]
  if (!pos) return null
  return pos.pcloud || null
}

/**
 * 按指定广告源获取广告位 ID（支付页多广告共存时使用，不受 adFlag 限制）
 * @param {string} positionKey
 * @param {'yunzong'|'bosite'|'pcloud'|'qunfeng'} sourceKey
 */
export function getAdPositionIdBySource (positionKey, sourceKey) {
  const pos = AD_POSITIONS[positionKey]
  if (!pos) {
    console.warn('[AdConfig] 未知广告位:', positionKey)
    return null
  }
  return pos[sourceKey] || null
}